<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  inheritAttrs: false,
});
</script>
<script setup lang="ts">
import { $getBrandingCampaignsWithOrdersSimpleList, $getCampaignSimpleList } from '@/api/campaigns'
import { uniqBy } from 'lodash'
import VTreeview from './VTreeview.vue'



interface IProps {
  modelValue?: any,
  label?: string,
  type?: any,
  workOrdersIncluded?: boolean,
  isSelectAllVisible?: boolean,
  eventGroup?: any,
  selectOptions?: any[] | null
}

interface WorkOrder {
  id: number
  name?: string
}

interface Campaign {
  id: number
  name: string
  work_orders?: WorkOrder[]
  selectedWorkOrders?: number[]
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => [],
  label: 'Project',
  type: null,
  workOrdersIncluded: false,
  isSelectAllVisible: false,
  eventGroup: null,
  selectOptions: null,
})

interface CustomEvents {
  (e: 'update:modelValue', newValue: any): void
}

const emit = defineEmits<CustomEvents>()

const attrs = useAttrs()
const isMenuOpen = ref<boolean>(false)
const campaigns = ref<Campaign[]>([])
const campaignsCurrentPage = ref<number>(1)
const campaignsPerPage: number = 10
const campaignsSearchStr = ref<string>('')
const isNextCampaignsPageExist = ref<boolean>(false)
const campaignsLoading = ref<boolean>(false)
// For client-side filtering
const searchStr = ref<string>('')
const openedNodes = ref<string[]>([])
const selectedSummaryRef = ref<HTMLElement | null>(null)
const selectedChipTextRef = ref<HTMLElement | null>(null)
const isSelectedChipTruncated = ref(false)
let selectedSummaryResizeObserver: ResizeObserver | null = null

const campaignNodeValue = (campaignId: number) => `campaign:${campaignId}`

const firstSelectedCampaignTitle = computed(() => {
  const selectedCampaign = props.modelValue?.[0]
  return selectedCampaign?.name ?? selectedCampaign?.title ?? ''
})

const getWorkOrderTitle = (workOrder: WorkOrder) => {
  if (workOrder?.id !== undefined && workOrder?.name)
    return `${workOrder.id} - ${workOrder.name}`

  return `${workOrder?.id ?? workOrder?.name ?? 'Work Order'}`
}

const campaignOptions = computed<Campaign[]>(() => {
  if (props.selectOptions)
    return props.selectOptions as Campaign[]

  return campaigns.value
})

// Get filtered campaigns based on Local search (client-side filtering)
const filteredCampaigns = computed<Campaign[]>(() => {
  if (!searchStr.value)
    return campaignOptions.value

  const searchLower = searchStr.value.toLowerCase()

  return campaignOptions.value.filter(campaign => campaign.name?.toLowerCase().includes(searchLower))
})

const selectedSummary = computed(() => {
  if (!props.modelValue.length)
    return ''
  return ' '
})

const selectedCampaignIds = computed<number[]>(() => {
  return props.modelValue.map((campaign: Campaign) => campaign.id)
})

const selectedWorkOrdersByCampaign = computed<Record<number, number[]>>(() => {
  return props.modelValue.reduce((acc: Record<number, number[]>, campaign: Campaign) => {
    acc[campaign.id] = campaign.selectedWorkOrders ?? []

    return acc
  }, {})
})

const getCampaignWorkOrderIds = (campaign: Campaign) => {
  return (campaign.work_orders ?? []).map(workOrder => workOrder.id)
}

const isCampaignFullySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  if (!allWorkOrderIds.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  return allWorkOrderIds.every(workOrderId => selectedWorkOrders.includes(workOrderId))
}

const isCampaignPartiallySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return false

  const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  if (!allWorkOrderIds.length)
    return false

  const selectedCount = allWorkOrderIds.filter(workOrderId => selectedWorkOrders.includes(workOrderId)).length

  return selectedCount > 0 && selectedCount < allWorkOrderIds.length
}

const isCampaignAnySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  return selectedWorkOrders.length > 0
}

// Check if all filtered campaigns are selected
const allFilteredSelected = computed(() => {
  if (filteredCampaigns.value.length === 0)
    return false

  return filteredCampaigns.value.every(campaign => isCampaignFullySelected(campaign))
})

// Check if some filtered campaigns are selected
const someFilteredSelected = computed(() => {
  if (filteredCampaigns.value.length === 0)
    return false

  const hasAnySelected = filteredCampaigns.value.some(campaign => isCampaignAnySelected(campaign))

  return hasAnySelected && !allFilteredSelected.value
})

const isCampaignSelected = (campaignId: number) => {
  return props.modelValue.some((selected: Campaign) => selected.id === campaignId)
}

const isCampaignOpen = (campaignId: number) => {
  return openedNodes.value.includes(campaignNodeValue(campaignId))
}

const isWorkOrderSelected = (campaignId: number, workOrderId: number) => {
  return props.modelValue
    .find((selected: Campaign) => selected.id === campaignId)
    ?.selectedWorkOrders?.includes(workOrderId) ?? false
}

// Toggle select all for filtered campaigns
const toggleSelectAll = () => {
  if (allFilteredSelected.value || someFilteredSelected.value) {
    const newValue = props.modelValue.filter((selected: Campaign) =>
      !filteredCampaigns.value.some(campaign => campaign.id === selected.id)
    )

    emit('update:modelValue', newValue)

    return
  }

  const newValue = [...props.modelValue]

  filteredCampaigns.value.forEach(campaign => {
    if (!newValue.some((selected: Campaign) => selected.id === campaign.id)) {
      if (props.workOrdersIncluded && campaign.work_orders?.length) {
        newValue.push({
          ...campaign,
          selectedWorkOrders: getCampaignWorkOrderIds(campaign),
        })
      } else {
        newValue.push(campaign)
      }
    } else if (props.workOrdersIncluded && campaign.work_orders?.length) {
      const campaignIdx = newValue.findIndex((selected: Campaign) => selected.id === campaign.id)

      if (campaignIdx >= 0) {
        newValue[campaignIdx] = {
          ...newValue[campaignIdx],
          selectedWorkOrders: getCampaignWorkOrderIds(campaign),
        }
      }
    }
  })

  emit('update:modelValue', newValue)
}

const toggleCampaignOpen = (campaignId: number) => {
  const nodeValue = campaignNodeValue(campaignId)

  if (openedNodes.value.includes(nodeValue)) {
    openedNodes.value = openedNodes.value.filter(value => value !== nodeValue)

    return
  }

  openedNodes.value = [...openedNodes.value, nodeValue]
}

const toggleCampaignSelection = (campaign: Campaign) => {
  if (props.workOrdersIncluded && campaign.work_orders?.length) {
    const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
    const existingCampaign = props.modelValue.find((selected: Campaign) => selected.id === campaign.id)

    if (isCampaignFullySelected(campaign)) {
      emit('update:modelValue', props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id))

      return
    }

    const nextCampaign = {
      ...(existingCampaign ?? campaign),
      selectedWorkOrders: allWorkOrderIds,
    }

    emit('update:modelValue', [
      ...props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id),
      nextCampaign,
    ])

    return
  }

  if (isCampaignSelected(campaign.id)) {
    emit('update:modelValue', props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id))

    return
  }

  emit('update:modelValue', [...props.modelValue, campaign])
}

const toggleWorkOrderSelection = (campaign: Campaign, workOrderId: number) => {
  const otherCampaigns = props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id)
  const existingCampaign = props.modelValue.find((selected: Campaign) => selected.id === campaign.id)
  const nextCampaign = { ...(existingCampaign ?? campaign) }
  const selectedWorkOrders = new Set(nextCampaign.selectedWorkOrders ?? [])

  if (selectedWorkOrders.has(workOrderId))
    selectedWorkOrders.delete(workOrderId)
  else
    selectedWorkOrders.add(workOrderId)

  if (selectedWorkOrders.size)
    nextCampaign.selectedWorkOrders = [...selectedWorkOrders]
  else
    emit('update:modelValue', otherCampaigns)

  if (selectedWorkOrders.size)
    emit('update:modelValue', [...otherCampaigns, nextCampaign])
}

const getCampaigns = async () => {
  campaignsLoading.value = true

  const filters = {
    is_select: true,
    type: props.type,
    is_visible: true,
    event_group_or_children: props.eventGroup && props.eventGroup.id ? props.eventGroup.id : undefined,
    search: campaignsSearchStr.value,
    per_page: campaignsPerPage,
    page: campaignsCurrentPage.value,
  }

  try {
    const apiCall = props.workOrdersIncluded
      ? $getBrandingCampaignsWithOrdersSimpleList
      : $getCampaignSimpleList

    const { data, isNextPageExist } = await apiCall(filters)

    campaigns.value = uniqBy([...campaigns.value, ...data.value], 'id')
    isNextCampaignsPageExist.value = !!isNextPageExist.value
  }
  catch (e) {
    console.log(e)
  }
  finally {
    campaignsLoading.value = false
  }
}

const onFocusCampaignSelect = async () => {
  if (!campaigns.value.length && !props.selectOptions) {
    campaigns.value = []
    campaignsCurrentPage.value = 1
    await getCampaigns()
  }
}

const syncOpenedNodes = () => {
  if (!props.workOrdersIncluded)
    return

  const selectedCampaignNodes = props.modelValue
    .filter((campaign: Campaign) => campaign.selectedWorkOrders?.length)
    .map((campaign: Campaign) => campaignNodeValue(campaign.id))

  const searchedCampaignNodes = searchStr.value
    ? filteredCampaigns.value
      .filter(campaign => campaign.work_orders?.length)
      .map(campaign => campaignNodeValue(campaign.id))
    : []

  openedNodes.value = [...new Set([
    ...openedNodes.value,
    ...selectedCampaignNodes,
    ...searchedCampaignNodes,
  ])]
}

const onActivatorFocused = async (isFocused: boolean) => {
  if (!isFocused)
    return

  await onFocusCampaignSelect()
  syncOpenedNodes()
}

const onLoadMoreCampaigns = async () => {
  campaignsCurrentPage.value++
  await getCampaigns()
}

const clearAndGetCampaigns = (value: string) => {
  searchStr.value = value
  campaigns.value = []
  campaignsCurrentPage.value = 1
  campaignsSearchStr.value = value
  getCampaigns()
}

const handleCampaignSearch = (value: string) => {
  searchStr.value = value

  if (!props.selectOptions)
    handleBackendSearch(value)
}

const handleBackendSearch = useDebounceFn((value: string) => {
  if (!props.selectOptions)
    clearAndGetCampaigns(value)
}, 500)

const clearSelection = () => {
  emit('update:modelValue', [])
}

const updateSelectedChipOverflow = () => {
  const element = selectedChipTextRef.value
  isSelectedChipTruncated.value = !!element && element.scrollWidth > element.clientWidth
}

const onSearchInput = (value: string) => {
  handleCampaignSearch(value)
  syncOpenedNodes()
}

const onToggleWorkOrder = (payload: { campaign: Campaign, workOrderId: number }) => {
  toggleWorkOrderSelection(payload.campaign, payload.workOrderId)
}

watch(isMenuOpen, async isOpen => {
  if (!isOpen)
    return

  await onFocusCampaignSelect()
  syncOpenedNodes()
})

watch(() => props.modelValue, () => {
  syncOpenedNodes()
}, { deep: true, immediate: true })

watch(() => props.type, async () => {
  clearAndGetCampaigns('')
})

watch(
  [firstSelectedCampaignTitle, () => props.modelValue.length, isMenuOpen],
  () => {
    nextTick(updateSelectedChipOverflow)
  },
)

watch(
  selectedSummaryRef,
  (element, previousElement) => {
    if (!selectedSummaryResizeObserver) {
      selectedSummaryResizeObserver = new ResizeObserver(() => {
        updateSelectedChipOverflow()
      })
    }

    if (previousElement)
      selectedSummaryResizeObserver.unobserve(previousElement)

    if (element)
      selectedSummaryResizeObserver.observe(element)

    nextTick(updateSelectedChipOverflow)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  selectedSummaryResizeObserver?.disconnect()
  selectedSummaryResizeObserver = null
})

defineExpose({ clearAndGetCampaigns })
</script>

<template>
  <VMenu v-model="isMenuOpen" :close-on-content-click="false" location="bottom">
    <template #activator="{ props: menuActivatorProps }">
      <VTextField class='custom-text-field' v-bind="{ ...attrs, ...menuActivatorProps }" :label="label"
                  :model-value="selectedSummary" :append-inner-icon="isMenuOpen ? 'tabler-chevron-up' : 'tabler-chevron-down'"
                  readonly clearable @click:clear.stop="clearSelection" @update:focused="onActivatorFocused">
        <template #prepend-inner v-if="props.modelValue && props.modelValue.length">
          <div
            ref="selectedSummaryRef"
            class="multiple-campaigns-select__selection"
          >
            <VChip
              size="small"
              class="multiple-campaigns-select__chip"
            >
              <span
                ref="selectedChipTextRef"
                class="multiple-campaigns-select__chip-text"
              >
                {{ firstSelectedCampaignTitle }}
              </span>
              <VMenu
                v-if="isSelectedChipTruncated"
                activator="parent"
                location="top center"
                open-on-hover
                open-delay="100"
                close-delay="0"
                offset="4"
                class="default-tooltip"
              >
                <span class="px-2">
                  {{ firstSelectedCampaignTitle }}
                </span>
              </VMenu>
            </VChip>
            <span
              v-if="props.modelValue.length > 1"
              class="multiple-campaigns-select__others text-grey text-caption"
            >
              (+{{ props.modelValue.length - 1 }} others)
            </span>
          </div>
        </template>

      </VTextField>

    </template>


    <VCard class="multiple-campaigns-select__menu">
      <VCardText class="pb-2">
        <VTextField :model-value="searchStr" label="Search projects" clearable autofocus
                    @update:modelValue="onSearchInput" />
      </VCardText>

      <VListItem v-if="isSelectAllVisible && filteredCampaigns.length > 1" class="v-list-item--select-all"
                 @click="toggleSelectAll">
        <template #prepend>
          <VCheckboxBtn :model-value="allFilteredSelected" :indeterminate="someFilteredSelected" />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredCampaigns.length }} found)
        </VListItemTitle>
      </VListItem>

      <VDivider v-if="isSelectAllVisible && filteredCampaigns.length > 1" />

      <div v-if="campaignsLoading && !filteredCampaigns.length"
           class="d-flex justify-center align-center multiple-campaigns-select__status">
        Loading...
        <VProgressCircular class="margin-left-5" size="22" indeterminate />
      </div>

      <div v-else-if="!filteredCampaigns.length"
           class="d-flex justify-center align-center multiple-campaigns-select__status">
        Projects not found
      </div>

      <Treeview v-else :campaigns="filteredCampaigns" :work-orders-included="workOrdersIncluded"
                :opened-nodes="openedNodes" :selected-campaign-ids="selectedCampaignIds"
                :selected-work-orders-by-campaign="selectedWorkOrdersByCampaign" @toggle-campaign="toggleCampaignSelection"
                @toggle-open="toggleCampaignOpen" @toggle-work-order="onToggleWorkOrder" />

      <div v-if="isNextCampaignsPageExist" class="pa-4 pt-2">
        <VBtn color="default" block :disabled="!!campaignsLoading" @click="onLoadMoreCampaigns">
          Load more...
          <VProgressCircular v-if="!!campaignsLoading" class="margin-left-5" size="22" indeterminate color="white" />
        </VBtn>
      </div>
    </VCard>
  </VMenu>
</template>

<style lang="scss">
.multiple-campaigns-select__selection {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.multiple-campaigns-select__chip {
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;

  .v-chip__content {
    min-width: 0;
    max-width: 100%;
  }
}

.multiple-campaigns-select__chip-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiple-campaigns-select__others {
  flex: 0 0 auto;
  margin-left: 8px;
  white-space: nowrap;
}

.multi-select-v-divider {
  margin: 8px;
  display: block;
}

.multiple-campaigns-select__menu {
  min-width: 200px;
}

.multiple-campaigns-select__status {
  min-height: 88px;
  padding: 16px;
}

.multiple-campaigns-select__tree {
  max-height: 320px;
  overflow: auto;
  padding: 0 8px 8px;
}

.multiple-campaign\s-select__campaign {
  background-color: rgba(var(--v-theme-surface));

  &.selected-campaign {
    border-color: rgba(176, 0, 32, 0.5);
    background-color: rgb(var(--v-theme-primary), 0.1) !important;
    color: #b00020;

  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.multiple-campaigns-select__children {
  padding-left: 30px;
}

.multiple-campaigns-select__child {
  opacity: 0.95;

  &:hover {
    background-color: rgba(0, 0, 0, 0.01);
  }
}

// Select all option
.v-list-item--select-all {
  font-weight: 600;
  background-color: rgba(0, 0, 0, 0.04);

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }
}
</style>
